import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import {
  GetFutureGreenFeePreview,
  GetFuturePowerCartPreview,
  PutFutureGreenFees,
  PutFuturePowerCarts,
} from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";

import FutureBookingPricingModal from "./FutureBookingPricingModal";
import { TOpenFutureBookingPricingModalParams } from "./futureBookingPricingUtils";
import {
  createEmptySelectedBySection,
  FUTURE_BOOKING_PRICING_SECTION_ORDER,
  mapGreenFeePreviewParticipant,
  mapPowerCartPreviewParticipant,
  TFutureBookingPricingParticipant,
  TFutureBookingPricingSection,
  TFutureBookingPricingSectionId,
} from "./types";

type TUseFutureBookingPricingModalOptions = {
  onSuccess?: () => void;
};

type TGreenFeeSectionContext = {
  customerTypeId: number;
};

type TPowerCartSectionContext = {
  cartCustomerTypeId: number;
};

type TSectionContext = {
  green_fee?: TGreenFeeSectionContext;
  power_cart?: TPowerCartSectionContext;
};

function getEligibleParticipantIds(participants: TFutureBookingPricingParticipant[]) {
  return participants.filter(participant => participant.eligible).map(participant => participant.participant_id);
}

function buildSuccessMessage(
  t: (key: string, options?: Record<string, unknown>) => string,
  greenFeeUpdatedCount: number,
  powerCartUpdatedCount: number,
) {
  const totalUpdatedCount = greenFeeUpdatedCount + powerCartUpdatedCount;

  if (totalUpdatedCount === 0) {
    return "";
  }

  return totalUpdatedCount === 1
    ? t("Updated information on 1 future booking")
    : t("Updated information on {{count}} future bookings", { count: totalUpdatedCount });
}

export default function useFutureBookingPricingModal(options: TUseFutureBookingPricingModalOptions = {}) {
  const { onSuccess } = options;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [applying, setApplying] = useState(false);
  const [sectionContexts, setSectionContexts] = useState<TSectionContext>({});
  const [sections, setSections] = useState<TFutureBookingPricingSection[]>([]);
  const [selectedBySection, setSelectedBySection] = useState(createEmptySelectedBySection);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setCustomerId(null);
    setApplying(false);
    setSectionContexts({});
    setSections([]);
    setSelectedBySection(createEmptySelectedBySection());
  }, []);

  const openIfNeeded = useCallback(
    async ({ customerId: nextCustomerId, customerTypeId, cartCustomerTypeId }: TOpenFutureBookingPricingModalParams) => {
      if (!customerTypeId && !cartCustomerTypeId) {
        return false;
      }

      const previewRequests: Array<Promise<void>> = [];
      const nextSections: TFutureBookingPricingSection[] = [];
      const nextSectionContexts: TSectionContext = {};
      const nextSelectedBySection = createEmptySelectedBySection();

      if (customerTypeId) {
        previewRequests.push(
          (async () => {
            const previewRes = await GetFutureGreenFeePreview(
              {
                customer_id: nextCustomerId,
                customer_type_id: customerTypeId,
              },
              true,
            );

            if (previewRes.status !== StatusCode.OK) {
              throw new Error(
                typeof previewRes.data === "string"
                  ? previewRes.data
                  : t("Error loading future booking green fee preview"),
              );
            }

            const previewParticipants = (previewRes.data?.participants ?? []).map(mapGreenFeePreviewParticipant);
            const hasEligibleParticipant = previewParticipants.some(participant => participant.eligible);

            if (!hasEligibleParticipant) {
              return;
            }

            nextSectionContexts.green_fee = { customerTypeId };
            nextSelectedBySection.green_fee = getEligibleParticipantIds(previewParticipants);
            nextSections.push({
              id: "green_fee",
              title: t("Green fees"),
              feeColumnLabel: t("Green fee"),
              participants: previewParticipants,
              selectedParticipantIds: [],
            });
          })(),
        );
      }

      if (cartCustomerTypeId) {
        previewRequests.push(
          (async () => {
            const previewRes = await GetFuturePowerCartPreview(
              {
                customer_id: nextCustomerId,
                cart_customer_type_id: cartCustomerTypeId,
              },
              true,
            );

            if (previewRes.status !== StatusCode.OK) {
              throw new Error(
                typeof previewRes.data === "string"
                  ? previewRes.data
                  : t("Error loading future booking power cart preview"),
              );
            }

            const previewParticipants = (previewRes.data?.participants ?? []).map(mapPowerCartPreviewParticipant);
            const hasEligibleParticipant = previewParticipants.some(participant => participant.eligible);

            if (!hasEligibleParticipant) {
              return;
            }

            nextSectionContexts.power_cart = { cartCustomerTypeId };
            nextSelectedBySection.power_cart = getEligibleParticipantIds(previewParticipants);
            nextSections.push({
              id: "power_cart",
              title: t("Power carts"),
              feeColumnLabel: t("Power cart"),
              participants: previewParticipants,
              selectedParticipantIds: [],
            });
          })(),
        );
      }

      try {
        await Promise.all(previewRequests);
      } catch (error) {
        dispatch(showError(error instanceof Error ? error.message : t("Error loading future booking preview")));
        return false;
      }

      if (nextSections.length === 0) {
        return false;
      }

      nextSections.sort(
        (left, right) =>
          FUTURE_BOOKING_PRICING_SECTION_ORDER.indexOf(left.id) -
          FUTURE_BOOKING_PRICING_SECTION_ORDER.indexOf(right.id),
      );

      setCustomerId(nextCustomerId);
      setSectionContexts(nextSectionContexts);
      setSelectedBySection(nextSelectedBySection);
      setSections(nextSections);
      setIsOpen(true);
      return true;
    },
    [dispatch, t],
  );

  const toggleParticipant = useCallback(
    (sectionId: TFutureBookingPricingSectionId, participantId: number, checked: boolean) => {
      setSelectedBySection(prev => ({
        ...prev,
        [sectionId]: checked
          ? [...prev[sectionId], participantId]
          : prev[sectionId].filter(id => id !== participantId),
      }));
    },
    [],
  );

  const toggleSelectAll = useCallback((sectionId: TFutureBookingPricingSectionId, checked: boolean) => {
    setSelectedBySection(prev => {
      const section = sections.find(currentSection => currentSection.id === sectionId);

      return {
        ...prev,
        [sectionId]: checked ? getEligibleParticipantIds(section?.participants ?? []) : [],
      };
    });
  }, [sections]);

  const handleApply = useCallback(async () => {
    if (!customerId) {
      return;
    }

    setApplying(true);

    let greenFeeUpdatedCount = 0;
    let powerCartUpdatedCount = 0;

    const greenFeeParticipantIds = selectedBySection.green_fee ?? [];
    const greenFeeContext = sectionContexts.green_fee;

    if (greenFeeParticipantIds.length > 0 && greenFeeContext) {
      const res = await PutFutureGreenFees(
        {
          customer_id: customerId,
          customer_type_id: greenFeeContext.customerTypeId,
          participant_ids: greenFeeParticipantIds,
        },
        true,
      );

      if (res.status !== StatusCode.OK) {
        setApplying(false);
        dispatch(showError(typeof res.data === "string" ? res.data : t("Error updating future booking green fees")));
        return;
      }

      greenFeeUpdatedCount = res.data?.updated_count ?? 0;
    }

    const powerCartParticipantIds = selectedBySection.power_cart ?? [];
    const powerCartContext = sectionContexts.power_cart;

    if (powerCartParticipantIds.length > 0 && powerCartContext) {
      const res = await PutFuturePowerCarts(
        {
          customer_id: customerId,
          cart_customer_type_id: powerCartContext.cartCustomerTypeId,
          participant_ids: powerCartParticipantIds,
        },
        true,
      );

      if (res.status !== StatusCode.OK) {
        setApplying(false);
        dispatch(showError(typeof res.data === "string" ? res.data : t("Error updating future booking power carts")));
        return;
      }

      powerCartUpdatedCount = res.data?.updated_count ?? 0;
    }

    setApplying(false);

    const successMessage = buildSuccessMessage(t, greenFeeUpdatedCount, powerCartUpdatedCount);

    if (successMessage) {
      dispatch(showSuccess(successMessage));
    }

    onSuccess?.();
    closeModal();
  }, [closeModal, customerId, dispatch, onSuccess, sectionContexts, selectedBySection, t]);

  const modalSections = useMemo(
    () =>
      sections.map(section => ({
        ...section,
        selectedParticipantIds: selectedBySection[section.id] ?? [],
      })),
    [sections, selectedBySection],
  );

  const modal = useMemo(
    () => (
      <FutureBookingPricingModal
        open={isOpen}
        sections={modalSections}
        applying={applying}
        onClose={closeModal}
        onApply={handleApply}
        onToggleParticipant={toggleParticipant}
        onToggleSelectAll={toggleSelectAll}
      />
    ),
    [applying, closeModal, handleApply, isOpen, modalSections, toggleParticipant, toggleSelectAll],
  );

  return {
    openIfNeeded,
    closeModal,
    modal,
    isOpen,
  };
}
